import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Power } from "lucide-react";
import { toast } from "sonner";

function MerchantTraffic() {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    api.get("/merchant/traffic").then((r) => setEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const toggle = async () => {
    try {
      const { data } = await api.post("/merchant/traffic", { enabled: !enabled });
      setEnabled(data.enabled);
      toast.success(data.enabled ? "Traffic started — your UPI IDs are live" : "Traffic stopped — all your UPI IDs are paused");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (enabled === null) return null;
  return (
    <Button
      size="sm"
      variant={enabled ? "destructive" : "default"}
      onClick={toggle}
      data-testid="merchant-traffic-toggle-button"
    >
      <Power className="mr-1.5 h-4 w-4" />
      {enabled ? "Stop traffic" : "Start traffic"}
    </Button>
  );
}

export function TopBalance() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canSee = ["super_admin", "admin"].includes(user.role);
  const canToggle = user.role === "super_admin";

  const load = useCallback(() => {
    if (!canSee) return;
    api.get("/platform/overview").then((r) => setData(r.data)).catch(() => {});
  }, [canSee]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try {
      const { data: d } = await api.post("/platform/traffic", { enabled: !data.traffic_enabled });
      setData((p) => ({ ...p, traffic_enabled: d.traffic_enabled }));
      toast.success(d.traffic_enabled ? "Traffic resumed" : "Traffic stopped");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (user.role === "merchant") return <MerchantTraffic />;
  if (!canSee || !data) return null;
  const neg = data.current_balance < 0;

  return (
    <div className="hidden md:flex items-center gap-4" data-testid="top-balance">
      <div className="text-right leading-tight">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current balance</p>
        <p className={`font-mono text-sm font-semibold ${neg ? "text-destructive" : "text-success"}`} data-testid="current-balance">
          {inr(data.current_balance)}
        </p>
      </div>
      <div className="text-right leading-tight">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Reserve</p>
        <p className="font-mono text-sm font-semibold" data-testid="reserve-amount">{inr(data.reserve)}</p>
      </div>
      {canToggle && (
        <Button
          size="sm"
          variant={data.traffic_enabled ? "destructive" : "default"}
          onClick={toggle}
          data-testid="traffic-toggle-button"
        >
          <Power className="mr-1.5 h-4 w-4" />
          {data.traffic_enabled ? "Stop traffic" : "Resume traffic"}
        </Button>
      )}
      {!canToggle && (
        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${data.traffic_enabled ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}>
          {data.traffic_enabled ? "Traffic live" : "Traffic stopped"}
        </span>
      )}
    </div>
  );
}
